import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import axios from 'axios'
import { toast } from 'react-toastify'
import config from '../../config'

const LOGIN_METHODS = ['Mobile', 'Email', 'Both']

const toForm = user => ({
  Name: user?.username || '',
  MobileNo: user?.mobileno || '',
  EmailID: user?.email || '',
  RoleID: user?.roleId != null ? String(user.roleId) : '',
  ProjectID: user?.ProjectID != null ? String(user.ProjectID) : '',
  Wing: user?.Wing != null ? String(user.Wing) : '',
  RoomNo: user?.RoomNo || '',
  LoginMethod: user?.LoginMethod || 'Mobile'
})

// Admin dialog to edit every field of a user (PUT /user/update/:id).
// `user` is a row from the Manage users table; `rolls` and `inputData`
// are the same role / project-wing lists the page already loads.
const Editusermodal = ({ show, user, rolls = [], inputData = [], onClose, onSaved }) => {
  const [form, setForm] = useState(toForm(user))
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  // reset the form whenever a different user is opened
  useEffect(() => {
    if (show) {
      setForm(toForm(user))
      setErrors({})
    }
  }, [show, user])

  const projects = useMemo(() => {
    const map = new Map()
    inputData.forEach(i => map.set(String(i.projectId), i.projectName))
    return [...map.entries()]
  }, [inputData])

  const wings = useMemo(
    () => inputData.filter(i => !form.ProjectID || String(i.projectId) === String(form.ProjectID)),
    [inputData, form.ProjectID]
  )

  const set = field => e => {
    const value = e.target.value
    setForm(prev => ({ ...prev, [field]: value, ...(field === 'ProjectID' ? { Wing: '' } : {}) }))
    setErrors(prev => ({ ...prev, [field]: undefined }))
  }

  const validate = () => {
    const e = {}
    if (!form.Name.trim()) e.Name = 'Name is required'
    if (form.MobileNo && !/^\+?\d{10,15}$/.test(form.MobileNo.trim())) e.MobileNo = 'Enter a valid 10–15 digit mobile number'
    if (form.EmailID && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.EmailID.trim())) e.EmailID = 'Enter a valid email address'
    if ((form.LoginMethod === 'Mobile' || form.LoginMethod === 'Both') && !form.MobileNo.trim()) e.MobileNo = 'Mobile number is needed for this login method'
    if ((form.LoginMethod === 'Email' || form.LoginMethod === 'Both') && !form.EmailID.trim()) e.EmailID = 'Email is needed for this login method'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!user || !validate()) return

    setSaving(true)
    try {
      const res = await axios.put(`${config.apiUrl}/user/update/${user.id}`, form, {
        headers: { Authorization: localStorage.getItem('token') }
      })
      toast.success('User updated')
      onSaved(res.data?.data)
      onClose()
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update user'
      // show clash errors next to the right field
      if (/mobile/i.test(msg)) setErrors(prev => ({ ...prev, MobileNo: msg }))
      else if (/email/i.test(msg)) setErrors(prev => ({ ...prev, EmailID: msg }))
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const field = (name, label, input) => (
    <div className="mb-3">
      <label className="form-label" htmlFor={`edit-user-${name}`}>{label}</label>
      {input}
      {errors[name] && <div className="invalid-feedback d-block">{errors[name]}</div>}
    </div>
  )

  return (
    <Modal show={show} onHide={saving ? () => {} : onClose} centered scrollable backdrop="static" aria-labelledby="edit-user-title">
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton closeLabel="Close">
          <Modal.Title as="h2" id="edit-user-title" className="h3 mb-0">
            Edit user
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {field('Name', <>Name <span className="text-danger" aria-hidden="true">*</span></>,
            <input id="edit-user-Name" className={`form-control${errors.Name ? ' is-invalid' : ''}`} value={form.Name} onChange={set('Name')} aria-required="true" />
          )}

          <div className="row g-3">
            <div className="col-sm-6">
              {field('MobileNo', 'Mobile number',
                <input id="edit-user-MobileNo" type="tel" inputMode="tel" className={`form-control${errors.MobileNo ? ' is-invalid' : ''}`} value={form.MobileNo} onChange={set('MobileNo')} />
              )}
            </div>
            <div className="col-sm-6">
              {field('EmailID', 'Email',
                <input id="edit-user-EmailID" type="email" className={`form-control${errors.EmailID ? ' is-invalid' : ''}`} value={form.EmailID} onChange={set('EmailID')} />
              )}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-sm-6">
              {field('RoleID', 'Role',
                <select id="edit-user-RoleID" className="form-select" value={form.RoleID} onChange={set('RoleID')}>
                  <option value="">Select role</option>
                  {rolls.map(r => <option key={r.rollId} value={String(r.rollId)}>{r.rollName}</option>)}
                </select>
              )}
            </div>
            <div className="col-sm-6">
              {field('LoginMethod', 'Login method',
                <select id="edit-user-LoginMethod" className="form-select" value={form.LoginMethod} onChange={set('LoginMethod')}>
                  {LOGIN_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              )}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-sm-6">
              {field('ProjectID', 'Project',
                projects.length > 0 ? (
                  <select id="edit-user-ProjectID" className="form-select" value={form.ProjectID} onChange={set('ProjectID')}>
                    <option value="">No project</option>
                    {projects.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                ) : (
                  <input id="edit-user-ProjectID" className="form-control" value={form.ProjectID} onChange={set('ProjectID')} />
                )
              )}
            </div>
            <div className="col-sm-6">
              {field('Wing', 'Wing',
                wings.length > 0 ? (
                  <select id="edit-user-Wing" className="form-select" value={form.Wing} onChange={set('Wing')}>
                    <option value="">No wing</option>
                    {wings.map(w => <option key={`${w.projectId}-${w.wingId}`} value={String(w.wingId)}>{w.wingName}</option>)}
                  </select>
                ) : (
                  <input id="edit-user-Wing" className="form-control" value={form.Wing} onChange={set('Wing')} />
                )
              )}
            </div>
          </div>

          {field('RoomNo', 'Bldg no. / Flat no.',
            <input id="edit-user-RoomNo" className="form-control" value={form.RoomNo} onChange={set('RoomNo')} placeholder="e.g. B / 402" />
          )}
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving && <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default Editusermodal
